import React from "react";
import { Box, Divider, Modal } from "@mui/material";
import { X } from "lucide-react";

/* Sample report preview (Property Audit Report by default, or the Survey Maps
   Land Measurement Report with kind="measurement").
   Extracted from HeroSection so every primary-CTA block on the page can open
   the same "View sample report" modal, one source of truth for the secondary
   action. PDFs are served from public buckets. */
const SAMPLES = {
  audit: {
    badge: "Property Audit",
    heading: "Sample Report Overview",
    intro:
      "A Property Audit Report is a consolidated, professionally curated report that includes all essential documents and verified records related to a property. It is designed to support informed buying, selling, financing, or legal validation of property by offering a single source of truth.",
    covers: [
      "Encumbrance Certificate",
      "Municipal Property Records",
      "RERA Project Details",
      "Property Tax",
      "Mutation Certificate",
      "Electricity Bill",
      "Prohibited Land",
      "Market Value",
    ],
    sections: [
      "Government records: Property location, survey, and zoning details",
      "Ownership history from Encumbrance Certificate",
      "Municipal data: GHMC/CDMA linkage, plan approvals, and occupancy",
      "RERA info (if applicable): Project registration and builder details",
      "Property tax: PTIN, payment history, and dues",
      "Electricity records: Connection details and billing summary",
    ],
    note: "Property Investment Overview & Summary: Covers key document checks, flags legal issues (if any), and provides a compliance score, ensuring the property is ready for resale, loan, or investment.",
    pdf: "https://jaaga-public-1722574714.s3.ap-south-1.amazonaws.com/otherimages/Sample_Audit_Report.pdf",
    pdfTitle: "Sample Property Audit Report",
  },
  measurement: {
    badge: "Survey Maps",
    heading: "Sample Land Measurement Report",
    intro:
      "Mark your plot on the satellite map in JaaGa Survey Maps and download a Land Measurement Report: the plot's area, boundary and survey numbers on one printable PDF, ready to share with a buyer, bank or surveyor.",
    covers: [
      "Total Area",
      "Perimeter & Corners",
      "Survey Number",
      "Village & Mandal",
      "Boundary Survey Nos.",
      "Plot Sketch to Scale",
      "Satellite View",
      "Corner Coordinates",
    ],
    sections: [
      "Schedule of property: survey number, village, mandal, district and centre point",
      "Extent in acres, guntas, cents, sq yd, sq ft and m²",
      "Neighbouring survey number on each side (north, south, east, west)",
      "Plot sketch to scale with numbered corners and side lengths",
      "Satellite view with the plot and survey lines drawn on it",
      "Boundary sides with direction, and GPS coordinates of every corner",
    ],
    note: "Indicative only, not a legal survey: survey lines come from the government cadastral map and can be off by a few metres. Confirm with a licensed surveyor and the village records before any transaction.",
    pdf: "https://pub-862ddacb791042a3b324aba5d9a12e44.r2.dev/documents/samples/land-measurement-report-sample.pdf",
    pdfTitle: "Sample Land Measurement Report",
  },
};

const SampleReportModal = ({ open, onClose, kind = "audit" }) => {
  const s = SAMPLES[kind] || SAMPLES.audit;
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="jh-sample-report-title"
      className="backdrop-blur-sm border-none select-none"
    >
      <Box
        className="modal-center select-none relative outline-none"
        sx={{
          height: "82vh",
          width: { xs: "94vw", md: "88vw", lg: "76vw" },
          maxWidth: "1220px",
          borderRadius: "18px",
          border: "1px solid rgba(234,232,226,0.9)",
          background:
            "radial-gradient(ellipse 820px 320px at 50% -180px,rgba(255,169,0,0.09) 0%,transparent 62%),#ffffff",
          boxShadow: "0 28px 70px -30px rgba(13,31,53,0.42)",
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          aria-label="Close sample report"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 grid place-items-center w-9 h-9 rounded-full bg-white/90 border border-[#eae8e2] cursor-pointer hover:bg-[#fff5d9] focus-visible:outline-2 focus-visible:outline-[#ffa900] focus-visible:outline-offset-2"
        >
          <X size={18} color="#153568" />
        </button>

        <div className="w-full h-full flex flex-col lg:flex-row">
          {/* Left panel, description */}
          <div className="w-full lg:w-[48%] h-full p-4 md:p-6 lg:p-7 overflow-y-auto border-b lg:border-b-0 lg:border-r border-[#eae8e2]">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#e2c16a] bg-[#fff5d9] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9a6b00] shadow-[0_1px_3px_rgba(154,107,0,0.12)]">
              {s.badge}
            </div>

            <h3
              id="jh-sample-report-title"
              className="mt-3 text-xl md:text-2xl font-bold tracking-tight text-[#153568]"
            >
              {s.heading}
            </h3>

            <p className="mt-3 text-[14px] leading-relaxed text-[#51627f]">
              {s.intro}
            </p>

            <Divider className="!my-4" />

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-2.5 list-disc pl-5 text-[13px] text-[#3a4a5c]">
              {s.covers.map((title, idx) => (
                <li key={idx}>{title}</li>
              ))}
            </ul>

            <Divider className="!my-4" />

            <ul className="grid grid-cols-1 list-decimal pl-5 text-[13px] leading-relaxed text-[#3a4a5c]">
              {s.sections.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>

            <Divider className="!my-4" />

            <div className="rounded-xl border border-[rgba(255,169,0,0.24)] bg-[#fef9ee] px-3.5 py-3 text-[12.5px] leading-relaxed text-[#51627f]">
              {s.note}
            </div>
          </div>

          {/* Right panel, PDF preview */}
          <div className="w-full lg:w-[52%] h-full p-3 md:p-4 lg:p-5 bg-[#f8f9fb]">
            <div className="w-full h-full rounded-xl overflow-hidden border border-[#e4e8ef] shadow-[0_12px_34px_-22px_rgba(21,53,104,0.36)] bg-white">
              <iframe
                src={s.pdf}
                className="w-full h-full border-0"
                title={s.pdfTitle}
              />
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default SampleReportModal;
